import React, { ReactElement } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Chip,
    Button,
    Checkbox,
    FormControlLabel,
} from '@material-ui/core'
import { ViewModule } from '@material-ui/icons'

import { getServiceImageDetails } from '../../../models/WorkOrder/services'
import { hasPermission } from '../../../models/Users/services'
import { SEE_VENDOR_FINANCES } from '../../../models/Permissions/types'
import { ServiceCategoryCode, User } from '../../../models'

interface Props {
    open: boolean
    hiddenColumns: Set<string>
    filteredPrimaryHeaders: Array<{
        name: string | React.ReactNode
        startColumn: number
        endColumn: number
        style: React.CSSProperties
    }>
    filteredColumnSchema: Array<{
        key: string
        name: string
        type: string
    }>
    maxBedrooms: number
    uniqueServices: string[]
    serviceMap: Record<
        string,
        {
            id: number
            name: string
            service_category?: ServiceCategoryCode | null
        }
    >
    workspaceUser?: User
    onClose: () => void
    toggleColumnVisibility: (columnKey: string) => void
    showAllColumns: () => void
}

// Helper function to safely extract text from React elements
const extractTextFromReactElement = (node: React.ReactNode): string => {
    if (typeof node === 'string') {
        return node
    }
    if (typeof node === 'number') {
        return node.toString()
    }
    if (React.isValidElement(node)) {
        const element = node as ReactElement
        if (element.props && element.props.children) {
            return extractTextFromReactElement(element.props.children)
        }
    }
    if (Array.isArray(node)) {
        return node.map(extractTextFromReactElement).join(' ')
    }
    return ''
}

export const ColumnVisibilityModal = ({
    open,
    onClose,
    hiddenColumns,
    toggleColumnVisibility,
    showAllColumns,
    filteredPrimaryHeaders,
    filteredColumnSchema,
    maxBedrooms,
    uniqueServices,
    serviceMap,
    workspaceUser,
}: Props) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
            }}
        >
            <DialogTitle
                style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef',
                    padding: '20px 24px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <ViewModule
                            style={{ color: '#6c757d', fontSize: '24px' }}
                        />
                        <Typography
                            variant="h6"
                            style={{ fontWeight: 600, color: '#212529' }}
                        >
                            Column Visibility Settings
                        </Typography>
                    </div>
                    {hiddenColumns.size > 0 && (
                        <Chip
                            label={`${hiddenColumns.size} hidden`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            style={{
                                backgroundColor: '#fff3cd',
                                borderColor: '#ffc107',
                                color: '#856404',
                            }}
                        />
                    )}
                </div>
            </DialogTitle>

            <DialogContent style={{ padding: '24px' }}>
                <div style={{ marginBottom: '24px' }}>
                    <Typography
                        variant="body1"
                        style={{ color: '#6c757d', lineHeight: 1.5 }}
                    >
                        Select which columns to show or hide in the spreadsheet
                        view. Changes are applied immediately and saved
                        automatically.
                    </Typography>
                </div>

                {/* Current Column Layout Summary */}
                {filteredPrimaryHeaders.length > 0 && (
                    <div
                        style={{
                            marginBottom: '32px',
                            padding: '20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e9ecef',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                marginBottom: '16px',
                                fontWeight: 600,
                                color: '#495057',
                            }}
                        >
                            Current Column Layout
                        </Typography>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                            }}
                        >
                            {filteredPrimaryHeaders.map((header, index) => {
                                // Extract the service name from React elements or use the string directly
                                const headerName = extractTextFromReactElement(
                                    header.name,
                                )

                                return (
                                    <Chip
                                        key={index}
                                        label={`${headerName} (${
                                            header.startColumn + 1
                                        }-${header.endColumn + 1})`}
                                        size="medium"
                                        variant="outlined"
                                        style={{
                                            backgroundColor:
                                                header.style.backgroundColor,
                                            borderColor:
                                                header.style.backgroundColor,
                                            color: '#1976d2',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                            height: '32px',
                                        }}
                                        title={
                                            header.name === 'Unit Info' &&
                                            !hiddenColumns.has('services')
                                                ? 'Includes Services column'
                                                : undefined
                                        }
                                    />
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Unit Information - Consolidated */}
                <div
                    style={{
                        marginBottom: '28px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid #e9ecef',
                    }}
                >
                    <Typography
                        variant="h6"
                        style={{
                            marginBottom: '20px',
                            color: '#495057',
                            fontWeight: 600,
                        }}
                    >
                        Unit Information
                    </Typography>

                    {/* Basic Unit Info */}
                    <div style={{ marginBottom: '20px' }}>
                        <Typography
                            variant="subtitle2"
                            style={{
                                marginBottom: '12px',
                                color: '#6c757d',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                fontSize: '12px',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Basic Information
                        </Typography>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '12px',
                            }}
                        >
                            {[
                                { key: 'unit_location', name: 'Location' },
                                { key: 'unit_name', name: 'Unit' },
                                { key: 'unit_config_name', name: 'Unit Type' },
                                {
                                    key: 'occupiable_areas_count',
                                    name: '# Beds',
                                },
                            ].map((column) => (
                                <FormControlLabel
                                    key={column.key}
                                    control={
                                        <Checkbox
                                            checked={
                                                !hiddenColumns.has(column.key)
                                            }
                                            onChange={() =>
                                                toggleColumnVisibility(
                                                    column.key,
                                                )
                                            }
                                            color="primary"
                                            style={{ marginRight: '8px' }}
                                        />
                                    }
                                    label={
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <span style={{ fontWeight: 500 }}>
                                                {column.name}
                                            </span>
                                            {!hiddenColumns.has(column.key) && (
                                                <Chip
                                                    label={`Col ${
                                                        filteredColumnSchema.findIndex(
                                                            (col) =>
                                                                col.key ===
                                                                column.key,
                                                        ) + 1
                                                    }`}
                                                    size="small"
                                                    variant="outlined"
                                                    style={{
                                                        fontSize: '10px',
                                                        height: '20px',
                                                        backgroundColor:
                                                            '#f8f9fa',
                                                        borderColor: '#6c757d',
                                                        color: '#6c757d',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    }
                                    style={{ margin: 0 }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Bedroom Status */}
                    {maxBedrooms > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <Typography
                                variant="subtitle2"
                                style={{
                                    marginBottom: '12px',
                                    color: '#6c757d',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    fontSize: '12px',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Bedroom Status
                            </Typography>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fit, minmax(150px, 1fr))',
                                    gap: '12px',
                                }}
                            >
                                {Array.from({ length: maxBedrooms }, (_, i) => {
                                    const bedLetter = String.fromCharCode(
                                        65 + i,
                                    )
                                    const columnKey = `bed_${bedLetter}_status_tag`
                                    return (
                                        <FormControlLabel
                                            key={columnKey}
                                            control={
                                                <Checkbox
                                                    checked={
                                                        !hiddenColumns.has(
                                                            columnKey,
                                                        )
                                                    }
                                                    onChange={() =>
                                                        toggleColumnVisibility(
                                                            columnKey,
                                                        )
                                                    }
                                                    color="primary"
                                                    style={{
                                                        marginRight: '8px',
                                                    }}
                                                />
                                            }
                                            label={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Bed {bedLetter}
                                                    </span>
                                                    {!hiddenColumns.has(
                                                        columnKey,
                                                    ) && (
                                                        <Chip
                                                            label={`Col ${
                                                                filteredColumnSchema.findIndex(
                                                                    (col) =>
                                                                        col.key ===
                                                                        columnKey,
                                                                ) + 1
                                                            }`}
                                                            size="small"
                                                            variant="outlined"
                                                            style={{
                                                                fontSize:
                                                                    '10px',
                                                                height: '20px',
                                                                backgroundColor:
                                                                    '#f8f9fa',
                                                                borderColor:
                                                                    '#6c757d',
                                                                color:
                                                                    '#6c757d',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            }
                                            style={{ margin: 0 }}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* Services Overview */}
                    <div>
                        <Typography
                            variant="subtitle2"
                            style={{
                                marginBottom: '12px',
                                color: '#6c757d',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                fontSize: '12px',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Services Overview
                        </Typography>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '12px',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!hiddenColumns.has('services')}
                                        onChange={() =>
                                            toggleColumnVisibility('services')
                                        }
                                        color="primary"
                                        style={{ marginRight: '8px' }}
                                    />
                                }
                                label={
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <span style={{ fontWeight: 500 }}>
                                            Services (Service Dots)
                                        </span>
                                        {!hiddenColumns.has('services') && (
                                            <Chip
                                                label={`Col ${
                                                    filteredColumnSchema.findIndex(
                                                        (col) =>
                                                            col.key ===
                                                            'services',
                                                    ) + 1
                                                }`}
                                                size="small"
                                                variant="outlined"
                                                style={{
                                                    fontSize: '10px',
                                                    height: '20px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderColor: '#6c757d',
                                                    color: '#6c757d',
                                                }}
                                            />
                                        )}
                                    </div>
                                }
                                style={{ margin: 0 }}
                            />
                        </div>
                    </div>
                </div>

                {/* Service Columns */}
                <div style={{ marginBottom: '24px' }}>
                    <Typography
                        variant="h6"
                        style={{
                            marginBottom: '20px',
                            color: '#495057',
                            fontWeight: 600,
                        }}
                    >
                        Service Columns
                    </Typography>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(400px, 1fr))',
                            gap: '24px',
                        }}
                    >
                        {uniqueServices.map((service) => (
                            <div
                                key={service}
                                style={{
                                    padding: '20px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '1px solid #e9ecef',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 600,
                                            color: '#495057',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {(() => {
                                            // Get service icon and color
                                            const serviceData = Object.values(
                                                serviceMap,
                                            ).find((s) => s.name === service)
                                            const serviceIcon = getServiceImageDetails(
                                                serviceData?.id ?? -1,
                                                serviceData?.service_category,
                                            ).icon
                                            return (
                                                <>
                                                    <img
                                                        src={serviceIcon}
                                                        style={{
                                                            maxHeight: 20,
                                                            maxWidth: 22,
                                                            filter:
                                                                'brightness(0.8)',
                                                        }}
                                                        alt={service}
                                                    />
                                                    <span
                                                        style={{
                                                            color: '#495057',
                                                        }}
                                                    >
                                                        {service}
                                                    </span>
                                                </>
                                            )
                                        })()}
                                    </Typography>

                                    {/* Quick Service Toggle */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const serviceColumns = [
                                                    `${service.toLowerCase()}_assigned_to`,
                                                    `${service.toLowerCase()}_start_date`,
                                                    `${service.toLowerCase()}_end_date`,
                                                    `${service.toLowerCase()}_created_date`,
                                                    `${service.toLowerCase()}_status`,
                                                    `${service.toLowerCase()}_service_areas`,
                                                    ...(hasPermission(
                                                        workspaceUser,
                                                        SEE_VENDOR_FINANCES,
                                                    )
                                                        ? [
                                                              `${service.toLowerCase()}_cost`,
                                                          ]
                                                        : []),
                                                ]

                                                const allHidden = serviceColumns.every(
                                                    (col) =>
                                                        hiddenColumns.has(col),
                                                )

                                                if (allHidden) {
                                                    // Show all columns for this service
                                                    serviceColumns.forEach(
                                                        (col) => {
                                                            if (
                                                                hiddenColumns.has(
                                                                    col,
                                                                )
                                                            ) {
                                                                toggleColumnVisibility(
                                                                    col,
                                                                )
                                                            }
                                                        },
                                                    )
                                                } else {
                                                    // Hide all columns for this service
                                                    serviceColumns.forEach(
                                                        (col) => {
                                                            if (
                                                                !hiddenColumns.has(
                                                                    col,
                                                                )
                                                            ) {
                                                                toggleColumnVisibility(
                                                                    col,
                                                                )
                                                            }
                                                        },
                                                    )
                                                }
                                            }}
                                            style={{
                                                fontSize: '12px',
                                                padding: '4px 12px',
                                                minWidth: 'auto',
                                                textTransform: 'none',
                                            }}
                                        >
                                            {(() => {
                                                const serviceColumns = [
                                                    `${service.toLowerCase()}_assigned_to`,
                                                    `${service.toLowerCase()}_start_date`,
                                                    `${service.toLowerCase()}_end_date`,
                                                    `${service.toLowerCase()}_created_date`,
                                                    `${service.toLowerCase()}_status`,
                                                    `${service.toLowerCase()}_service_areas`,
                                                    ...(hasPermission(
                                                        workspaceUser,
                                                        SEE_VENDOR_FINANCES,
                                                    )
                                                        ? [
                                                              `${service.toLowerCase()}_cost`,
                                                          ]
                                                        : []),
                                                ]

                                                const allHidden = serviceColumns.every(
                                                    (col) =>
                                                        hiddenColumns.has(col),
                                                )
                                                const someHidden = serviceColumns.some(
                                                    (col) =>
                                                        hiddenColumns.has(col),
                                                )

                                                if (allHidden) {
                                                    return 'Show All'
                                                } else if (someHidden) {
                                                    return 'Hide All'
                                                } else {
                                                    return 'Hide All'
                                                }
                                            })()}
                                        </Button>

                                        {/* Status indicator */}
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            style={{
                                                fontSize: '10px',
                                                height: '20px',
                                                backgroundColor: (() => {
                                                    const serviceColumns = [
                                                        `${service.toLowerCase()}_assigned_to`,
                                                        `${service.toLowerCase()}_start_date`,
                                                        `${service.toLowerCase()}_end_date`,
                                                        `${service.toLowerCase()}_created_date`,
                                                        `${service.toLowerCase()}_status`,
                                                        `${service.toLowerCase()}_service_areas`,
                                                        ...(hasPermission(
                                                            workspaceUser,
                                                            SEE_VENDOR_FINANCES,
                                                        )
                                                            ? [
                                                                  `${service.toLowerCase()}_cost`,
                                                              ]
                                                            : []),
                                                    ]

                                                    const visibleCount = serviceColumns.filter(
                                                        (col) =>
                                                            !hiddenColumns.has(
                                                                col,
                                                            ),
                                                    ).length
                                                    const totalCount =
                                                        serviceColumns.length

                                                    if (visibleCount === 0)
                                                        return '#f8d7da'
                                                    if (
                                                        visibleCount ===
                                                        totalCount
                                                    )
                                                        return '#d4edda'
                                                    return '#fff3cd'
                                                })(),
                                                borderColor: (() => {
                                                    const serviceColumns = [
                                                        `${service.toLowerCase()}_assigned_to`,
                                                        `${service.toLowerCase()}_start_date`,
                                                        `${service.toLowerCase()}_end_date`,
                                                        `${service.toLowerCase()}_created_date`,
                                                        `${service.toLowerCase()}_status`,
                                                        `${service.toLowerCase()}_service_areas`,
                                                        ...(hasPermission(
                                                            workspaceUser,
                                                            SEE_VENDOR_FINANCES,
                                                        )
                                                            ? [
                                                                  `${service.toLowerCase()}_cost`,
                                                              ]
                                                            : []),
                                                    ]

                                                    const visibleCount = serviceColumns.filter(
                                                        (col) =>
                                                            !hiddenColumns.has(
                                                                col,
                                                            ),
                                                    ).length
                                                    const totalCount =
                                                        serviceColumns.length

                                                    if (visibleCount === 0)
                                                        return '#dc3545'
                                                    if (
                                                        visibleCount ===
                                                        totalCount
                                                    )
                                                        return '#28a745'
                                                    return '#ffc107'
                                                })(),
                                                color: (() => {
                                                    const serviceColumns = [
                                                        `${service.toLowerCase()}_assigned_to`,
                                                        `${service.toLowerCase()}_start_date`,
                                                        `${service.toLowerCase()}_end_date`,
                                                        `${service.toLowerCase()}_created_date`,
                                                        `${service.toLowerCase()}_status`,
                                                        `${service.toLowerCase()}_service_areas`,
                                                        ...(hasPermission(
                                                            workspaceUser,
                                                            SEE_VENDOR_FINANCES,
                                                        )
                                                            ? [
                                                                  `${service.toLowerCase()}_cost`,
                                                              ]
                                                            : []),
                                                    ]

                                                    const visibleCount = serviceColumns.filter(
                                                        (col) =>
                                                            !hiddenColumns.has(
                                                                col,
                                                            ),
                                                    ).length
                                                    const totalCount =
                                                        serviceColumns.length

                                                    if (visibleCount === 0)
                                                        return '#721c24'
                                                    if (
                                                        visibleCount ===
                                                        totalCount
                                                    )
                                                        return '#155724'
                                                    return '#856404'
                                                })(),
                                            }}
                                            label={`${(() => {
                                                const serviceColumns = [
                                                    `${service.toLowerCase()}_assigned_to`,
                                                    `${service.toLowerCase()}_start_date`,
                                                    `${service.toLowerCase()}_end_date`,
                                                    `${service.toLowerCase()}_created_date`,
                                                    `${service.toLowerCase()}_status`,
                                                    `${service.toLowerCase()}_service_areas`,
                                                    ...(hasPermission(
                                                        workspaceUser,
                                                        SEE_VENDOR_FINANCES,
                                                    )
                                                        ? [
                                                              `${service.toLowerCase()}_cost`,
                                                          ]
                                                        : []),
                                                ]

                                                const visibleCount = serviceColumns.filter(
                                                    (col) =>
                                                        !hiddenColumns.has(col),
                                                ).length
                                                const totalCount =
                                                    serviceColumns.length

                                                return `${visibleCount}/${totalCount}`
                                            })()} visible`}
                                        />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '10px',
                                    }}
                                >
                                    {[
                                        {
                                            key: `${service.toLowerCase()}_assigned_to`,
                                            name: 'Assigned To',
                                        },
                                        {
                                            key: `${service.toLowerCase()}_start_date`,
                                            name: 'Start Date',
                                        },
                                        {
                                            key: `${service.toLowerCase()}_end_date`,
                                            name: 'End Date',
                                        },
                                        {
                                            key: `${service.toLowerCase()}_created_date`,
                                            name: 'Created Date',
                                        },
                                        {
                                            key: `${service.toLowerCase()}_status`,
                                            name: 'Status',
                                        },
                                        {
                                            key: `${service.toLowerCase()}_service_areas`,
                                            name: 'Service Areas',
                                        },
                                        ...(hasPermission(
                                            workspaceUser,
                                            SEE_VENDOR_FINANCES,
                                        )
                                            ? [
                                                  {
                                                      key: `${service.toLowerCase()}_cost`,
                                                      name: 'Cost',
                                                  },
                                              ]
                                            : []),
                                    ].map((column) => (
                                        <FormControlLabel
                                            key={column.key}
                                            control={
                                                <Checkbox
                                                    checked={
                                                        !hiddenColumns.has(
                                                            column.key,
                                                        )
                                                    }
                                                    onChange={() =>
                                                        toggleColumnVisibility(
                                                            column.key,
                                                        )
                                                    }
                                                    color="primary"
                                                    style={{
                                                        marginRight: '8px',
                                                    }}
                                                />
                                            }
                                            label={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {column.name}
                                                    </span>
                                                    {!hiddenColumns.has(
                                                        column.key,
                                                    ) && (
                                                        <Chip
                                                            label={`Col ${
                                                                filteredColumnSchema.findIndex(
                                                                    (col) =>
                                                                        col.key ===
                                                                        column.key,
                                                                ) + 1
                                                            }`}
                                                            size="small"
                                                            variant="outlined"
                                                            style={{
                                                                fontSize:
                                                                    '10px',
                                                                height: '20px',
                                                                backgroundColor:
                                                                    '#f8f9fa',
                                                                borderColor:
                                                                    '#6c757d',
                                                                color:
                                                                    '#6c757d',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            }
                                            style={{ margin: 0 }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>

            <DialogActions
                style={{
                    padding: '20px 24px',
                    backgroundColor: '#f8f9fa',
                    borderTop: '1px solid #e9ecef',
                }}
            >
                <Button
                    onClick={showAllColumns}
                    variant="outlined"
                    color="secondary"
                    style={{
                        fontWeight: 500,
                        textTransform: 'none',
                        padding: '8px 24px',
                    }}
                >
                    Show All Columns
                </Button>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    style={{
                        fontWeight: 500,
                        textTransform: 'none',
                        padding: '8px 24px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
